/**
 * Display-only helper for transaction history.
 *
 * Some guest records get anonymized by the nightly retention purge
 * (firstName/lastName set to "[purged]"). Returning guests who scan
 * AFTER their record was purged will produce NEW transactions that
 * inherit "[purged]" as the snapshot name. This helper falls through
 * to the next-best label so the UI never shows the sentinel string.
 *
 * Priority (per the 2026-05-16 spec — extended after operator
 * reported limited-tx rows showing "Purged Guest" for guests that
 * had been anonymized prior to the night's check-in):
 *
 *   1. transaction.firstName + transaction.lastName  (snapshot at write time)
 *   2. transaction.stageName                          (when non-NONE / non-purged)
 *   3. fallbackGuest.firstName + lastName             (live join — caller may pass)
 *   4. fallbackGuest.stageName                        (live join stage name)
 *   5. "Guest #<guestID>"                             (last-ditch disambiguator)
 *   6. "Unknown Guest"                                (no usable identifier)
 *
 * Notes:
 *   - Step (5) is intentionally placed AFTER the join because a real
 *     human-readable name is always preferable to a guestID, but
 *     before (6) so the operator at least gets a row-level identifier
 *     instead of a generic placeholder. The Transaction History UI
 *     groups rows by guestID under the hood (allAccess aggregation)
 *     so this label remains stable across multiple rows for the same
 *     guest.
 *   - The label changed from "Purged Guest" to "Unknown Guest"
 *     because "Purged" looked like an error state to staff; "Unknown
 *     Guest" reads as a missing-data state which matches the spec's
 *     "Avoid showing 'Purged' in Transaction History unless required
 *     by law/privacy logic" guidance.
 */

const isPurgedSentinel = (s?: string | null): boolean => {
    if (!s) return true;
    const trimmed = s.trim();
    if (!trimmed) return true;
    if (trimmed === '[purged]') return true;
    if (/^purged$/i.test(trimmed)) return true;
    return false;
};

const isPlaceholderStage = (s?: string | null): boolean => {
    if (isPurgedSentinel(s)) return true;
    const t = (s || '').trim().toUpperCase();
    return t === 'NONE' || t === '—' || t === '-';
};

export interface NameSource {
    firstName?: string | null;
    lastName?: string | null;
    stageName?: string | null;
    guestID?: string | null;
}

/** First+last from a transaction-like object, or null if both look purged. */
export function txFullName(
    tx: NameSource | null | undefined
): string | null {
    if (!tx) return null;
    const fn = isPurgedSentinel(tx.firstName) ? '' : (tx.firstName || '').trim();
    const ln = isPurgedSentinel(tx.lastName) ? '' : (tx.lastName || '').trim();
    if (!fn && !ln) return null;
    return `${fn}${fn && ln ? ' ' : ''}${ln}`;
}

/** Stage name if usable (non-placeholder, non-purged), else null. */
function usableStage(src: NameSource | null | undefined): string | null {
    if (!src) return null;
    if (isPlaceholderStage(src.stageName)) return null;
    return (src.stageName || '').trim();
}

/** Guest #id fallback. Returns null when no usable guestID. */
function guestIdLabel(
    tx: NameSource | null | undefined,
    fallback?: NameSource | null,
): string | null {
    const id = (tx?.guestID || fallback?.guestID || '').trim();
    if (!id) return null;
    if (isPurgedSentinel(id)) return null;
    return `Guest #${id}`;
}

const UNKNOWN_GUEST = 'Unknown Guest';

/**
 * Pick the best display string for a transaction row.
 *
 * @param tx            the transaction (preferred source)
 * @param fallback      optional live guest record for legacy rows whose
 *                      snapshot is empty/missing (not for overriding a
 *                      good tx snapshot)
 */
export function displayTransactionName(
    tx: NameSource | null | undefined,
    fallback?: NameSource | null
): string {
    // 1. real names on the tx snapshot
    const full = txFullName(tx);
    if (full) return full;
    // 2. stage name on the tx snapshot
    const stage = usableStage(tx);
    if (stage) return stage;
    // 3. real names on the joined guest profile
    const fallbackFull = txFullName(fallback);
    if (fallbackFull) return fallbackFull;
    // 4. stage name on the joined guest profile
    const fallbackStage = usableStage(fallback);
    if (fallbackStage) return fallbackStage;
    // 5. last-ditch disambiguator
    const id = guestIdLabel(tx, fallback);
    if (id) return id;
    // 6. nothing usable
    return UNKNOWN_GUEST;
}

/** Same priority chain but biased toward stageName first (for allAccess
 *  / roster rows that display the stage name as the headline). */
export function displayStageThenName(
    tx: NameSource | null | undefined,
    fallback?: NameSource | null
): string {
    // Stage first (whichever source has one wins).
    const stage = usableStage(tx);
    if (stage) return stage;
    // Then real names from tx.
    const full = txFullName(tx);
    if (full) return full;
    // Then stage from the joined guest profile.
    const fallbackStage = usableStage(fallback);
    if (fallbackStage) return fallbackStage;
    // Then names from the joined guest profile.
    const fallbackFull = txFullName(fallback);
    if (fallbackFull) return fallbackFull;
    // Then guestID disambiguator.
    const id = guestIdLabel(tx, fallback);
    if (id) return id;
    return UNKNOWN_GUEST;
}
